import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './StartupGuide.css';
import { FaFacebook, FaMedium, FaTwitter, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import Header from './components/Header'; 

function StartupGuide() {
  const location = useLocation(); 
  const [expandedSections, setExpandedSections] = useState({
    intro: true, // Keep intro expanded by default
    accountSettings: false,
    quicknotes: false,
    explorerBoard: false,
    packIt: false,
    mapFeatures: false,
    tips: false,
    troubleshooting: false,
    conclusion: false
  });

  // State for subsections - using a nested object structure
  const [expandedSubsections, setExpandedSubsections] = useState({});

  // Helper function to get all subsection keys for each section
  const getAllSubsectionKeys = (sectionKey) => {
    const allSubsections = {
      intro: [], // No subsections in intro
      accountSettings: ['account-2.1', 'account-2.2', 'account-2.3', 'account-2.4', 'account-2.5'],
      quicknotes: [
        'quicknotes-3.1', 'quicknotes-3.1-method1', 'quicknotes-3.1-method2',
        'quicknotes-3.2', 'quicknotes-3.3', 'quicknotes-3.4', 'quicknotes-3.4-examples', 'quicknotes-3.4-colors',
        'quicknotes-3.5', 'quicknotes-3.5-filters', 'quicknotes-3.5-pagination', 'quicknotes-3.5-convert',
        'quicknotes-3.6'
      ],
      explorerBoard: ['explorer-4.1', 'explorer-4.2', 'explorer-4.3', 'explorer-4.4', 'explorer-4.5', 'explorer-4.6', 'explorer-4.7'],
      packIt: [
        'packit-5.1', 'packit-5.2', 'packit-5.2-method1', 'packit-5.2-method2', 'packit-5.2-method3', 'packit-5.2-method4',
        'packit-5.3', 'packit-5.3-listview', 'packit-5.3-details', 'packit-5.3-weather', 'packit-5.3-packing', 'packit-5.3-chat',
        'packit-5.4', 'packit-5.4-filters', 'packit-5.4-editing', 'packit-5.4-convert',
        'packit-5.5'
      ],
      mapFeatures: ['map-6.1', 'map-6.2', 'map-6.3', 'map-6.4'],
      tips: ['tips-7.1', 'tips-7.2', 'tips-7.3', 'tips-7.4', 'tips-7.5'],
      troubleshooting: [
        'troubleshooting-8.1', 'troubleshooting-password', 'troubleshooting-map-notes', 'troubleshooting-journeys-active',
        'troubleshooting-journey-progress', 'troubleshooting-edit-notes', 'troubleshooting-delete-packit', 'troubleshooting-journey-diff',
        'troubleshooting-8.2', 'troubleshooting-8.3'
      ],
      conclusion: [] // No subsections in conclusion
    };
    return allSubsections[sectionKey] || [];
  };

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
    
    // Expand all subsections for intro section (which is expanded by default)
    const introSubsections = getAllSubsectionKeys('intro');
    if (introSubsections.length > 0) {
      const expandedState = {};
      introSubsections.forEach(key => {
        expandedState[key] = true;
      });
      setExpandedSubsections(prev => ({
        ...prev,
        ...expandedState
      }));
    }
  }, [location]);

  const toggleSection = (sectionKey) => {
    const willBeExpanded = !expandedSections[sectionKey];
    
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
    
    // When expanding a section, expand all subsections
    if (willBeExpanded) {
      const allSubsectionKeys = getAllSubsectionKeys(sectionKey);
      if (allSubsectionKeys.length > 0) {
        const expandedState = {};
        allSubsectionKeys.forEach(key => {
          expandedState[key] = true;
        });
        setExpandedSubsections(prev => ({
          ...prev,
          ...expandedState
        }));
      }
    }
  };

  const toggleSubsection = (subsectionKey) => {
    setExpandedSubsections(prev => ({
      ...prev,
      [subsectionKey]: !prev[subsectionKey]
    }));
  };

  const SectionHeader = ({ sectionKey, title, children }) => (
    <div 
      className="section-header" 
      onClick={() => toggleSection(sectionKey)}
    >
      <h2>{title}</h2>
      <span className="expand-icon">
        {expandedSections[sectionKey] ? <FaChevronUp /> : <FaChevronDown />}
      </span>
    </div>
  );

  const SubsectionHeader = ({ subsectionKey, title, level = 3 }) => {
    const isExpanded = expandedSubsections[subsectionKey] === true; // Default to collapsed (only true = expanded)
    
    return (
      <div 
        className={`subsection-header subsection-level-${level}`}
        onClick={() => toggleSubsection(subsectionKey)}
      >
        {level === 3 ? <h3>{title}</h3> : <h4>{title}</h4>}
        <span className="expand-icon subsection-icon">
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>
    );
  };

  const SubsectionContent = ({ subsectionKey, children }) => {
    const isExpanded = expandedSubsections[subsectionKey] === true; // Default to collapsed (only true = expanded)
    
    if (!isExpanded) return null;
    
    return (
      <div className="subsection-content-wrapper">
        {children}
      </div>
    );
  };

  const VideoPlayer = ({ src, title }) => {
    const [videoError, setVideoError] = useState(false);
    
    const handleVideoError = (e) => {
      console.error(`Failed to load video: ${src}`, e);
      setVideoError(true);
    };
    
    return (
      <div className="video-container">
        {!videoError ? (
          <video 
            src={src} 
            controls 
            className="guide-video"
            preload="metadata"
            onError={handleVideoError}
            playsInline
          >
            Your browser does not support the video tag.
            <a href={src}>Download video</a>
          </video>
        ) : (
          <div className="video-error">
            <p><strong>Video not available</strong></p>
            <p className="video-error-note">The video file "{src.split('/').pop()}" could not be loaded.</p>
            <p className="video-error-note">Please check the browser console for more details.</p>
            <a href={src} className="video-download-link" target="_blank" rel="noopener noreferrer">
              Try opening video directly
            </a>
          </div>
        )}
        {title && <p className="video-title">{title}</p>}
      </div>
    );
  };

  return (
    <div className="startup-guide">
      <Header currentPage="startup-guide" />

      <div className="content">
        <h1 className="page-title">Startup Guide</h1>
        <p className="intro">
          Welcome to Ideal Scribe! This comprehensive guide will help you master all aspects of our platform. 
          Ideal Scribe helps you capture notes, discover amazing travel journeys, and create personalized travel plans called Pack-Its. 
          Whether you're organizing ideas, planning trips, or tracking your adventures, this guide covers everything you need to know.
          <br /><br />
          <strong>Click on any section below to expand and learn more!</strong>
        </p>

        {/* Introduction Section */}
        <section className={`section ${expandedSections.intro ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="intro" title="1. Introduction to Ideal Scribe" />
          {expandedSections.intro && (
            <div className="section-content-wrapper">
          <div className="section-content">
            <div className="section-text">

                  <p>Ideal Scribe is your all-in-one platform for organizing ideas and planning amazing trips. Our platform consists of three main features:</p>
                  <ul>
                    <li><strong>Quick Notes:</strong> Capture and organize your ideas, thoughts, and inspirations with AI-powered categorization</li>
                    <li><strong>Explorer Board:</strong> Discover curated travel journeys and visualize them on interactive maps</li>
                    <li><strong>Pack-Its:</strong> Transform your notes and journeys into personalized travel plans</li>
                  </ul>
                  <p>All features work together seamlessly, allowing you to capture ideas, discover destinations, and plan your perfect trip—all in one place.</p>
                </div>
                <img src="images/startup-guide-overview.png" alt="Ideal Scribe main dashboard overview" className="illustration"/>
              </div>
            </div>
          )}
        </section>

        {/* Account Settings Section */}
        <section className={`section ${expandedSections.accountSettings ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="accountSettings" title="2. Getting Started: Account Settings" />
          {expandedSections.accountSettings && (
            <div className="section-content-wrapper">
          <div className="section-content">
            <div className="section-text">
                  <p>Before diving into the features, it's important to set up your account preferences. This helps Ideal Scribe provide personalized recommendations and better organize your content.</p>
                  
                  <VideoPlayer 
                    src="/videos/Step_2____Account_Settings.mp4" 
                    title="Watch: Setting Up Your Account"
                  />

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="account-2.1" title="2.1 Accessing Account Settings" />
                    <SubsectionContent subsectionKey="account-2.1">
                      <p>To access your account settings:</p>
                      <ol>
                        <li>Click "Account Settings" in the navigation menu at the top of any page</li>
                        <li>You'll see several sections to customize your experience</li>
                        <li>ideal Scribe will never share your personal information with anyone and is soley used for your personal use</li>
                      </ol>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="account-2.2" title="2.2 Setting Your Starting Location" />
                    <SubsectionContent subsectionKey="account-2.2">
                      <p>Your location is crucial for:</p>
                      <ul>
                        <li>Calculating distances to destinations</li>
                        <li>Suggesting nearby journeys</li>
                        <li>Providing travel distance information in Pack-Its</li>
                      </ul>
                      <p><strong>How to set it:</strong> Enter your home address in the Account Settings page. This location will be used as your starting point for all distance calculations.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="account-2.3" title="2.3 Customizing Color Categories" />
                    <SubsectionContent subsectionKey="account-2.3">
                      <p>Ideal Scribe uses color coding to categorize your notes. You can customize what each color represents:</p>
                      <ul>
                        <li><code>Blue</code> - Travel (default)</li>
                        <li><code>Green</code> - To do (default)</li>
                        <li><code>Red</code> - Shopping (default)</li>
                        <li><code>Purple</code> - Health (default)</li>
                        <li><code>Yellow</code> - Food (default)</li>
                        <li><code>Orange</code> - Entertainment (default)</li>
                        <li><code>Light Blue</code> - Reminder (default)</li>
              </ul>
                      <p>You can change these categories in Account Settings to match your personal preferences and workflow.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="account-2.4" title="2.4 Understanding Your Plan" />
                    <SubsectionContent subsectionKey="account-2.4">
                      <p>Ideal Scribe offers different plans to suit your travel planning needs. Here's what each plan includes:</p>

                      <h4>Adventurer Plan</h4>
                      <ul>
                        <li>Includes up to 500 notes for more extensive planning</li>
                        <li>Up to 10 active journeys</li>
                      </ul>

                      <h4>Globe Trotter Plan</h4>
                      <ul>
                        <li>Unlimited notes for all your travel ideas</li>
                        <li>Unlimited Pack-It creation for complete trip planning</li>
                        <li>Enhanced AI recommendations</li>
                        <li>Access to chatbot assistance</li>
                        <li>Up to 10 active journeys</li>
                      </ul>

                      <p>You can upgrade your plan anytime from Account Settings if you need more capacity for your travel planning adventures!</p>
                    </SubsectionContent>
                  </div>


                </div>
                <img src="images/startup-guide-account-settings.png" alt="Account Settings page" className="illustration"/>
              </div>
              
            </div>
          )}
        </section>

        {/* Quick Notes Section */}
        <section className={`section ${expandedSections.quicknotes ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="quicknotes" title="3. Quick Notes: Note Management" />
          {expandedSections.quicknotes && (
            <div className="section-content-wrapper">
              <div className="section-content">
                <div className="section-text">
                  <p>Notes are the foundation of your Ideal Scribe experience. They allow you to capture ideas, thoughts, travel inspirations, and any information you want to remember at a moments notice.</p>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="quicknotes-3.1" title="3.1 Creating Notes" />
                    <SubsectionContent subsectionKey="quicknotes-3.1">
                      <p>There are two main ways to create notes:</p>
                      


                      <div className="subsection">
                        <SubsectionHeader subsectionKey="quicknotes-3.1-method1" title="Method 1: Using the &quot;New Note&quot; Button" level={4} />
                        <SubsectionContent subsectionKey="quicknotes-3.1-method1">
                          <ol>
                            <li>Navigate to the Quick Notes page (click "Quick Notes" in the navigation)</li>
                            <li>Click the "New Note" button located in the top right of the page</li>
                            <li>Fill in your note details:
                              <ul>
                                <li>Note content/description</li>
                                <li>Tags (optional)</li>
                              </ul>
                            </li>
                            <li>Click "Save" to create your note</li>
                          </ol>
                          <VideoPlayer 
                        src="/videos/Step_3___Creating_a_Note.mp4" 
                        title="Watch: Creating a Note"
                      />
                        </SubsectionContent>
                      </div>




                      <div className="subsection">
                        <SubsectionHeader subsectionKey="quicknotes-3.1-method2" title="Method 2: Sending Notes via Email" level={4} />
                        <SubsectionContent subsectionKey="quicknotes-3.1-method2">
                          <p>For quick capture on the go, simply send an email to <strong>scribe@quicknotes.idealscribe.com</strong>:</p>
                          <ol>
                            <li>Compose an email with your note content</li>
                            <li>Send it to the email address above</li>
                            <li>Your note will automatically appear in your Ideal Scribe account</li>
                            <li>You can add tags in the email subject or body (see section 3.3 for tag formats)</li>
                          </ol>
                          <p><strong>Tip:</strong> Email notes are perfect for capturing ideas while traveling or when you don't have access to the web interface.</p>
                          <VideoPlayer 
                        src="/videos/Step_4___Create_Notes_via_Email.mp4" 
                        title="Watch: Creating Notes via Email"
                      />
                        </SubsectionContent>
                      </div>

                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-new-note-button.png" alt="Quick Notes page showing New Note button" className="illustration"/>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="quicknotes-3.2" title="3.2 AI-Powered Organization" />
                    <SubsectionContent subsectionKey="quicknotes-3.2">
                      <p>Ideal Scribe uses advanced artificial intelligence to automatically organize your notes:</p>
                      <ul>
                        <li><strong>Automatic Categorization:</strong> Our AI analyzes your note content and suggests relevant categories, making it easier to keep everything organized</li>
                        <li><strong>Location Extraction:</strong> Any location-related information in your notes is automatically identified and tagged, so you can always remember where events or activities are happening</li>
                        <li><strong>Best Time Suggestions:</strong> For activities or visits, our AI provides optimal times based on various factors, ensuring you make the most out of your plans</li>
                        <li><strong>Smart Tagging:</strong> The AI can suggest relevant tags based on your note content</li>
                      </ul>
                      <p>This intelligent automation helps you save time and ensures your notes are organized efficiently, allowing you to focus on what really matters.</p>
                    </SubsectionContent>
                  </div>
                </div>

              </div>

          <div className="section-content">
            <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="quicknotes-3.3" title="3.3 Using Manual Tags for Enhanced Organization" />
                    <SubsectionContent subsectionKey="quicknotes-3.3">
                      <p>To further refine AI suggestions and organize your notes exactly the way you want, you can use manual tags. These tags can be added directly within your notes using the following formats:</p>
              <ul>
                <li>Using a colon (<code>:</code>): <code>color: red</code></li>
                <li>Using a hash (<code>#</code>): <code>#color red</code></li>
              </ul>
              <p>For example, if you want to set the color of a note to red, you can include either of the following in your note:</p>
              <ul>
                <li><code>color: red</code></li>
                <li><code>#color red</code></li>
              </ul>
              <p>Manual tags give you the flexibility to customize your notes according to your preferences, making it easier to retrieve and categorize them later.</p>
                    </SubsectionContent>
                  </div>
            </div>
                <img src="images/tags.png" alt="Manual tags example" className="illustration"/>
          </div>

          <div className="section-content">
            <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="quicknotes-3.4" title="3.4 Complete List of Manual Tag Categories" />
                    <SubsectionContent subsectionKey="quicknotes-3.4">
                      <p>Here is a comprehensive list of tags you can use to manually tag and organize your notes:</p>
              <ul className="tag-list">
                <li><strong>#Activity</strong> (e.g., hike, concert, bike ride)</li>
                <li><strong>#Season</strong> (e.g., summer, fall, winter, spring)</li>
                        <li><strong>#color</strong> or <strong>#color-red</strong> (sets note color)</li>
                        <li><strong>#name</strong> (sets the note name/title)</li>
                        <li><strong>#description</strong> (adds a description)</li>
                        <li><strong>#address</strong> (adds a location address)</li>
                        <li><strong>#website</strong> (includes a website URL)</li>
                        <li><strong>#season</strong> (specifies the season)</li>
                        <li><strong>#month</strong> (indicates the month)</li>
                        <li><strong>#day</strong> (marks the day of the week)</li>
                        <li><strong>#time</strong> (adds a specific time)</li>
                        <li><strong>#nearby</strong> (mentions a nearby landmark)</li>
                        <li><strong>#tags</strong> (adds multiple tags)</li>
                        <li><strong>#url</strong> (includes a URL for reference)</li>
              </ul>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="quicknotes-3.4-examples" title="Tag Examples" level={4} />
                        <SubsectionContent subsectionKey="quicknotes-3.4-examples">
              <p>Here are some examples of how you can use these tags in your notes:</p>
              <ul className="examples">
                            <li><code>#Activity hike</code> - Tagging a note about a hiking trip</li>
                            <li><code>#Season summer</code> - Categorizing an event happening in the summer</li>
                            <li><code>#color-red</code> - Setting the color of a note to red for easy identification</li>
                            <li><code>#name John's Birthday</code> - Naming a note for John's birthday celebration</li>
                            <li><code>#description Company meeting at HQ</code> - Providing a description for a company meeting</li>
                            <li><code>#address 123 Main St, Springfield</code> - Adding an address for a meeting location</li>
                            <li><code>#website www.example.com</code> - Including a relevant website link in your note</li>
                            <li><code>#time 10:00 AM</code> - Adding a specific time to a note</li>
                            <li><code>#nearby Central Park</code> - Mentioning a nearby landmark for context</li>
                            <li><code>#tags important, work</code> - Adding multiple tags for better organization</li>
              </ul>
                        </SubsectionContent>
                      </div>


                    </SubsectionContent>
                  </div>
                </div>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="quicknotes-3.5" title="3.5 Managing Notes" />
                    <SubsectionContent subsectionKey="quicknotes-3.5">
                      <p>Once you've created notes, Ideal Scribe provides powerful tools to manage and organize them:</p>

                      <p>Set notes as favorites to easily find them later</p>
                      <p>Set notes as completed so they count rowards progress on your journeys</p>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="quicknotes-3.5-filters" title="Filters" level={4} />
                        <SubsectionContent subsectionKey="quicknotes-3.5-filters">
                          <p>Use filters to quickly find the notes you're looking for:</p>
                          <ul>
                            <li><strong>Color/Category:</strong> Filter by note color or category</li>
                            <li><strong>Date Range:</strong> Filter notes by creation date or date mentioned in the note</li>
                            <li><strong>Tags:</strong> Filter by specific tags you've added</li>
                            <li><strong>Location:</strong> Filter notes that have location information</li>
                            <li><strong>Search:</strong> Use the search bar to find notes by keywords</li>
                          </ul>
                          <p>Click the "Filters" button on the Quick Notes page to access all filtering options.</p>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="quicknotes-3.5-pagination" title="Pages" level={4} />
                        <SubsectionContent subsectionKey="quicknotes-3.5-pagination">
                          <p>If you have many notes, use the page controls at the bottom of the page to navigate through your notes. You can adjust how many notes are shown per page.</p>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="quicknotes-3.5-convert" title="Converting Notes to Pack-Its" level={4} />
                        <SubsectionContent subsectionKey="quicknotes-3.5-convert">
                          <p>One of the most powerful features is converting your notes into Pack-Its (Destination Exploration). To convert a note:</p>
                          <ol>
                            <li>Open the note you want to convert</li>
                            <li>Click the "Convert to Pack-It" button</li>
                            <li>Follow the Pack-It creation flow (see section 5 for details)</li>
                          </ol>
                          <p><strong>Tip:</strong> Notes recommendations will be automatically added as potential locations for your new Pack-It!</p>
                        </SubsectionContent>
                      </div>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-note-filters.png" alt="Note filters panel" className="illustration"/>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="quicknotes-3.6" title="3.6 Map Visualization" />
                    <SubsectionContent subsectionKey="quicknotes-3.6">
                      <p>All notes with location information are automatically displayed on the interactive map:</p>
                      <ul>
                        <li><strong>Color-Coded Markers:</strong> Each note appears as a marker on the map, color-coded by category</li>
                        <li><strong>Click to View:</strong> Click any marker to view the note details</li>
                        <li><strong>Create Notes from Map:</strong> Click on an empty area of the map to create a new note at that location</li>
                        <li><strong>Map Legend:</strong> Use the legend on the right side to understand what each color represents</li>
                      </ul>
                      <p>The map provides a visual way to see where your ideas and plans are located, making it easier to plan trips and discover connections between places.</p>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-notes-map.png" alt="Map showing note markers" className="illustration"/>
              </div>
            </div>
          )}
        </section>

        {/* Explorer Board Section */}
        <section className={`section ${expandedSections.explorerBoard ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="explorerBoard" title="4. Explorer Board: Discovering Journeys" />
          {expandedSections.explorerBoard && (
            <div className="section-content-wrapper">
              <div className="section-content">
                <div className="section-text">
                  <p>Journeys are curated bucket lists created by our community. They represent complete travel experiences with multiple destinations, activities, and recommendations. Create your own customized journey quickly and easily by simply submitting a prompt for ideal scribe to create the perfect journey for what you are feeling.</p>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="explorer-4.1" title="4.1 What are Journeys?" />
                    <SubsectionContent subsectionKey="explorer-4.1">
                      <p>Journeys are comprehensive bucket lists and can include examples such as:</p>
                      <ul>
                        <li>Visit all countries in the world</li>
                        <li>Visit all continents in the world</li>
                        <li>Visit all UNESCO World Heritage Sites</li>
                        <li>Dream up your own custom journey (ie: I want to visit the top 10 fishing spots in the world)</li>
                      </ul>
                      <p>Journeys help you discover new places and plan your travels. You can add journeys to your Explorer Board to keep track of places you want to visit.</p>
                    </SubsectionContent>
        
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="explorer-4.2" title="4.2 Browsing Journeys" />
                    <SubsectionContent subsectionKey="explorer-4.2">
                      <p>To browse available journeys:</p>
                      <ol>
                        <li>Click the "My Explorer Board" tab at the top of the page</li>
                        <li>You'll see a list of available journeys</li>
                        <li>Each journey card shows:
                          <ul>
                            <li>Journey name and description</li>
                            <li>Number of locations</li>
                            <li>Progress information</li>
                            <li>Category tags</li>
                          </ul>
                        </li>
                        <li>Click on a journey card to view detailed information</li>
                      </ol>
                      <img src="images/startup-guide-add-journey.png" alt="Add Journey button and interface" className="illustration"/>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-explorer-board.png" alt="Explorer Board page showing journey list" className="illustration"/>

              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="explorer-4.3" title="4.3 Adding Journeys" />
                    <SubsectionContent subsectionKey="explorer-4.3">
                      <p>There are two ways to work with journeys:</p>
                      <ul>
                        <li><strong>"Add Journey":</strong> Adds an existing journey from the system to your Explorer Board</li>
                        <li><strong>"Create Journey":</strong> Creates a brand new journey based on your preferences</li>
                      </ul>
                      <p>To add an existing journey:</p>
                      <ol>
                        <li>Browse the available journeys</li>
                        <li>Click "Add Journey" on any journey you're interested in</li>
                        <li>The journey will be added to your Explorer Board</li>
                        <li>It will appear on the map as a polygon overlay</li>
                      </ol>
                  
                      <p>To create a new journey:</p>
                      <ol>
                        <li>Select "Create Journey"</li>
                        <li>You can create a new journey by simply submitting a prompt and have Ideal Scribe create it for you.</li>
                        <li>or you can select the "Map Editor" to manually add locations to your journey via the map editor or inputting geoJSON via the advanced button.</li>
                      </ol>

                      <p>Once the journey is created, you will be prompted to review and approved the journey. This is your chance to edit the journey if needed.</p>
                      <p><strong>Tip:</strong> You can have multiple journeys active and visible at once, and they'll all be visible on the map, but only if the option is selected for that journey!</p>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-create-journey.png" alt="Add Journey button and interface" className="illustration"/>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="explorer-4.4" title="4.4 Journey Details" />
                    <SubsectionContent subsectionKey="explorer-4.4">
                      <p>When you click on a journey, you'll see detailed information:</p>
                      <ul>
                        <li><strong>Description:</strong> Overview of the journey</li>
                        <li><strong>Locations List:</strong> All destinations included in the journey</li>
                        <li><strong>Progress Tracking:</strong> See which locations you've visited</li>
                        <li><strong>Scope Information:</strong> Countries, states, or regions covered</li>
                        <li><strong>Desires, Feelings, Goals:</strong> Tags that describe the journey's character</li>
                        <li><strong>Recommended Season:</strong> Best times to visit</li>
                        <li><strong>Rating:</strong> Overall journey rating</li>
                      </ul>
                      <p>You can sort locations alphabetically, by distance from your home, or by distance from your closest note. Completed locations are marked with a checkmark.</p>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-journey-details.png" alt="Journey details view" className="illustration"/>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="explorer-4.5" title="4.5 Journey Settings" />
                    <SubsectionContent subsectionKey="explorer-4.5">
                    <p>Select the gear icon next to a journey to access the journey settings.</p>
                      <p>Use journey settings to customize your journey even further:</p>
                      <ul>
                        <li>Specify the color of your journey</li>
                        <li>Indicate if you want to highlight only visited locations, unvisited locations, or all locations</li>
                        <li>Manually specify the percentage of completion for your journey</li>
                      </ul>

                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-journey-settings.png" alt="Journey filters interface" className="illustration"/>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="explorer-4.6" title="4.6 Journey Map Visualization" />
                    <SubsectionContent subsectionKey="explorer-4.6">
                      <p>Journeys appear on the map as colored polygons, making it easy to visualize travel routes:</p>
                      <ul>
                        <li><strong>Overlays:</strong> Each journey is displayed as a polygon covering its geographic area</li>
                        <li><strong>Left-Click:</strong> Click on a journey polygon to view journey details</li>
                        <li><strong>Multiple Active Journeys:</strong> You can have up to 3 journeys active at once, all visible on the map</li>
                        <li><strong>Color Coding:</strong> Each journey has its own color for easy identification</li>
                      </ul>
                      <p>The map visualization helps you see how journeys relate to your notes and plan your travels more effectively.</p>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-explorer-board.png" alt="Map showing journey polygons" className="illustration"/>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="explorer-4.7" title="4.7 Understanding Journey Buttons" />
                    <SubsectionContent subsectionKey="explorer-4.7">
                      <p>Each journey card on your Explorer Board has several action buttons. Here's what each button does:</p>
                      
                      <ul>
                        <li><strong>Heart Icon (Favorite):</strong> Bookmark journeys you want to easily find later. Favorite journeys can be filtered in the Explorer Board.</li>
                        
                        <li><strong>Check Icon (Complete):</strong> Mark a journey as completed when you've finished visiting all locations. This helps you track which journeys you've accomplished.</li>
                        
                        <li><strong>Eye Icon (Display on Map):</strong> Toggle whether the journey polygon appears on the map. You can have multiple journeys displayed simultaneously to see how they overlap geographically.</li>
                        
                        <li><strong>Play Circle Icon (Set Active):</strong> <strong>IMPORTANT:</strong> This button is required for automatic progress calculation. Only active journeys automatically track your completion percentage by comparing your notes with journey locations. You can have a maximum of 3 journeys active at once. If you want progress to update automatically, make sure your journey is set to active.</li>
                        
                        <li><strong>Sync Icon (Recalculate Progress):</strong> Manually refresh the journey progress calculation. This compares your notes with the journey's locations and updates the completion percentage. Use this button when you've added new notes with location data and want to immediately update the progress, or if progress seems incorrect. The calculation may take a few moments to complete.</li>
                        
                        <li><strong>Gear Icon (Edit):</strong> Opens the journey settings modal where you can customize the journey color, display mode (show visited/unvisited/all locations), and manually set the completion percentage.</li>
                        
                        <li><strong>Trash Icon (Remove):</strong> Removes the journey from your Explorer Board. This does not delete the journey itself, just removes it from your personal board.</li>
                      </ul>
                      
                      <p><strong>Tip:</strong> Look for the small info icon (ℹ️) next to the "Set Active" and "Recalculate Progress" buttons for more detailed explanations. Hover over these icons to see additional information about why these buttons are important.</p>
                      
                      <p><strong>Understanding Active Status:</strong> The "Set Active" button is crucial for progress tracking. When a journey is active, Ideal Scribe automatically compares your notes (that have location information) with the journey's locations and calculates your completion percentage. If a journey is not active, it won't automatically update its progress, even if you add new notes. You can still manually set the completion percentage in the journey settings, but automatic tracking requires the journey to be active.</p>
                    </SubsectionContent>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Pack-It Section */}
        <section className={`section ${expandedSections.packIt ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="packIt" title="5. Pack-It: Destination Exploration" />
          {expandedSections.packIt && (
            <div className="section-content-wrapper">
              <div className="section-content">
                <div className="section-text">
                  <p>Pack-Its are your personalized travel plans that combine notes, journeys, and your preferences into comprehensive trip exploration packits automatically.</p>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="packit-5.1" title="5.1 What are Pack-Its?" />
                    <SubsectionContent subsectionKey="packit-5.1">
                      <p>Pack-Its help you plan complete trips by:</p>
                      <ul>
                        <li>Combining multiple destinations</li>
                        <li>Including activities from your notes</li>
                        <li>Creating packing lists</li>
                        <li>Providing trip organization and structure</li>
                        <li>Offering AI-powered trip planning assistance via chat</li>
                      </ul>
                      <p>You can create Pack-Its from scratch or convert existing notes into Pack-Its.</p>
                    </SubsectionContent>
                  </div>

                  <VideoPlayer 
                    src="/videos/Step_5___Creating_a_Packit.mp4" 
                    title="Watch: Creating a Pack-It"
                  />

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="packit-5.2" title="5.2 Creating Pack-Its" />
                    <SubsectionContent subsectionKey="packit-5.2">
                      <p>Ideal Scribe offers four different ways to create Pack-Its, each suited for different planning styles:</p>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.2-method1" title="Method 1: Quick Trip" level={4} />
                        <SubsectionContent subsectionKey="packit-5.2-method1">
                          <p>Perfect for simple trips with basic information:</p>
                          <ul>
                            <li>Enter trip name and description</li>
                            <li>Select start and end dates</li>
                            <li>Add destinations</li>
                            <li>Choose basic preferences</li>
                          </ul>
                          <p>Quick Trip is ideal when you know where you want to go and just need to organize the basics.</p>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.2-method2" title="Method 2: Custom Trip" level={4} />
                        <SubsectionContent subsectionKey="packit-5.2-method2">
                          <p>The most comprehensive option with all available features:</p>
                          <ul>
                            <li>Complete trip details</li>
                            <li>Multiple destinations with day counts</li>
                            <li>Travel mode preferences</li>
                            <li>Interests and activities</li>
                            <li>Packing needs</li>
                            <li>Alert preferences</li>
                          </ul>
                          <p>Use Custom Trip when you want full control over every aspect of your trip planning.</p>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.2-method3" title="Method 3: Trip Mood" level={4} />
                        <SubsectionContent subsectionKey="packit-5.2-method3">
                          <p>Plan based on your goals, desires, and feelings:</p>
                          <ul>
                            <li>Select your travel goals (Relaxation, Adventure, Learning, etc.)</li>
                            <li>Choose what you desire (Beach, Mountains, City, etc.)</li>
                            <li>Express your feelings about the trip</li>
                            <li>Let Ideal Scribe suggest destinations and activities</li>
                          </ul>
                          <p>Trip Mood is perfect when you know how you want to feel but aren't sure where to go.</p>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.2-method4" title="Method 4: Roll the Dice" level={4} />
                        <SubsectionContent subsectionKey="packit-5.2-method4">
                          <p>Let Ideal Scribe surprise you with a random trip:</p>
                          <ul>
                            <li>Automatically generates a 1-week trip</li>
                            <li>Starts next week</li>
                            <li>Includes random destinations and activities</li>
                          </ul>
                          <p>Perfect for spontaneous travelers who love surprises!</p>
                        </SubsectionContent>
                      </div>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-packit-creation-methods.png" alt="Pack-It creation method selection screen" className="illustration"/>
              </div>

              <div className="section-content">
                <div className="section-text">


                  <div className="subsection">
                    <SubsectionHeader subsectionKey="packit-5.3" title="5.3 Pack-It Features" />
                    <SubsectionContent subsectionKey="packit-5.3">
                      <p>Once created, Pack-Its provide comprehensive trip planning tools:</p>
                      <VideoPlayer 
                    src="/videos/Step_6___Packit_Review.mp4" 
                    title="Watch: Pack-It Review"
                  />

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.3-listview" title="Pack-It List View" level={4} />
                        <SubsectionContent subsectionKey="packit-5.3-listview">
                          <p>View all your Pack-Its in one place:</p>
                          <ul>
                            <li>See trip names, dates, and status</li>
                            <li>Filter and search your Pack-Its</li>
                            <li>Access trip details quickly</li>
                          </ul>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.3-details" title="Pack-It Details" level={4} />
                        <SubsectionContent subsectionKey="packit-5.3-details">
                          <p>Each Pack-It includes:</p>
                          <ul>
                            <li>Trip information and dates</li>
                            <li>Destinations with day counts</li>
                            <li>Activities and interests</li>
                            <li>Packing list</li>
                            <li>Trip preferences and settings</li>
                          </ul>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.3-weather" title="Weather Forecast" level={4} />
                        <SubsectionContent subsectionKey="packit-5.3-weather">
                          <p>The Weather tab shows forecasts for your Pack-It destinations. You can view by day or by location.</p>
                          <p><strong>Forecast ranges:</strong></p>
                          <ul>
                            <li><strong>1–14 days ahead:</strong> Detailed forecast data from real-time weather stations and short-range models</li>
                            <li><strong>Up to ~1 year ahead:</strong> Future weather data for longer-range trip planning</li>
                          </ul>
                          <p><strong>Data sources:</strong> Our provider combines global weather stations and high-resolution weather models (1–11 km) and uses AI to select the most reliable source for each location. Accuracy is highest for 1–3 days and remains useful for longer-range planning.</p>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.3-packing" title="Packing Lists" level={4} />
                        <SubsectionContent subsectionKey="packit-5.3-packing">
                          <p>Each Pack-It automatically generates a packing list based on:</p>
                          <ul>
                            <li>Trip duration</li>
                            <li>Destinations and activities</li>
                            <li>Travel mode</li>
                            <li>Your preferences</li>
                          </ul>
                          <p>You can customize the packing list, mark items as packed, and track your preparation progress.</p>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.3-chat" title="Chat Widget" level={4} />
                        <SubsectionContent subsectionKey="packit-5.3-chat">
                          <p>Every Pack-It includes an AI-powered chat assistant:</p>
                          <ul>
                            <li>Get trip planning advice</li>
                            <li>Ask questions about your destinations</li>
                            <li>Receive activity recommendations</li>
                            <li>Get help with packing lists</li>
                          </ul>
                          <p>The chat widget can be expanded or hidden as needed.</p>
                        </SubsectionContent>
                      </div>
                    </SubsectionContent>
                  </div>
                </div>
              </div>

              <div className="section-content">
                <div className="section-text">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="packit-5.4" title="5.4 Managing Pack-Its" />
                    <SubsectionContent subsectionKey="packit-5.4">
                      <p>Keep your Pack-Its organized with these management features:</p>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.4-filters" title="Filters and Search" level={4} />
                        <SubsectionContent subsectionKey="packit-5.4-filters">
                          <p>Use filters to find specific Pack-Its:</p>
                          <ul>
                            <li>Filter by trip name</li>
                            <li>Filter by date range</li>
                            <li>Filter by category or type</li>
                            <li>Search by keywords</li>
                          </ul>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.4-editing" title="Editing Pack-Its" level={4} />
                        <SubsectionContent subsectionKey="packit-5.4-editing">
                          <p>You can edit Pack-Its at any time to:</p>
                          <ul>
                            <li>Update trip details</li>
                            <li>Add or remove destinations</li>
                            <li>Modify dates</li>
                            <li>Update packing lists</li>
                            <li>Change preferences</li>
                          </ul>
                        </SubsectionContent>
                      </div>

                      <div className="subsection">
                        <SubsectionHeader subsectionKey="packit-5.4-convert" title="Converting Notes to Pack-Its" level={4} />
                        <SubsectionContent subsectionKey="packit-5.4-convert">
                          <p>Transform your notes into full Pack-Its:</p>
                          <ol>
                            <li>Open any note with location information</li>
                            <li>Click "Convert to Pack-It"</li>
                            <li>Follow the Pack-It creation flow</li>
                            <li>The note's information will be pre-filled in the Pack-It form</li>
                          </ol>
                          <p><strong>Tip:</strong> Notes with addresses or location tags are perfect for Pack-It conversion!</p>
                        </SubsectionContent>
                      </div>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="packit-5.5" title="5.5 Understanding AI-Generated Information" />
                    <SubsectionContent subsectionKey="packit-5.5">
                      <p>Ideal Scribe uses AI to generate detailed information for your Pack-Its, including:</p>
                      <ul>
                        <li><strong>Places to Eat:</strong> Restaurant details, contact information, hours, and menus</li>
                        <li><strong>Things to Do:</strong> Activity descriptions, availability, pricing, and booking requirements</li>
                        <li><strong>Places to Stay:</strong> Accommodation details, contact info, amenities, and booking URLs</li>
                        <li><strong>Chatbot Responses:</strong> Answers to your travel questions</li>
                      </ul>
                      
                      <p><strong>Important:</strong> While we strive for accuracy, AI-generated information may not always be 100% current or accurate. Information can change, and AI systems may occasionally make errors.</p>
                      
                      <p><strong>Best Practices:</strong></p>
                      <ul>
                        <li>Always verify contact information (phone numbers, addresses, websites) directly with businesses before your trip</li>
                        <li>Confirm hours of operation, availability, and pricing by contacting the business</li>
                        <li>Check booking requirements and cancellation policies directly with the provider</li>
                        <li>Use AI-generated information as a starting point, but verify critical details independently</li>
                      </ul>
                      
                      <p>You'll see subtle reminders throughout the Pack-It interface to verify contact information. These reminders help ensure you have the most current and accurate details for your trip.</p>
                      
                      <p>For complete terms regarding AI-generated content, please refer to our <Link to="/terms-of-service">Terms of Service</Link>.</p>
                    </SubsectionContent>
                  </div>
                </div>
                <img src="images/startup-guide-packit-chatbot.png" alt="Pack-It filters interface" className="illustration"/>
              </div>
            </div>
          )}
        </section>

        {/* Advanced Tips Section */}
        <section className={`section ${expandedSections.tips ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="tips" title="6. Advanced Tips & Best Practices" />
          {expandedSections.tips && (
            <div className="section-content-wrapper">
              <div className="section-text">
              <p>Get the most out of Ideal Scribe with these tips and best practices:</p>

              <div className="subsection">
                <SubsectionHeader subsectionKey="tips-6.1" title="7.1 Organizing Notes Effectively" />
                <SubsectionContent subsectionKey="tips-7.1">
                  <ul>
                    <li><strong>Use Consistent Tags:</strong> Develop a tagging system and stick to it for easier searching</li>
                    <li><strong>Add Locations:</strong> Always include location information when relevant—it enables map visualization and Pack-It conversion</li>
                    <li><strong>Color Categories:</strong> Customize color categories to match your workflow</li>
                    <li><strong>Regular Cleanup:</strong> Periodically review and organize your notes</li>
                  </ul>
                </SubsectionContent>
              </div>

              <div className="subsection">
                <SubsectionHeader subsectionKey="tips-6.2" title="6.2 Using Tags Strategically" />
                <SubsectionContent subsectionKey="tips-6.2">
                  <ul>
                    <li><strong>Combine Tags:</strong> Use multiple tags for better organization (e.g., <code>#Activity hike #Season summer</code>)</li>
                    <li><strong>Email Tags:</strong> Include tags in email notes for automatic organization</li>
                    <li><strong>Descriptive Names:</strong> Use clear, descriptive names for notes</li>
                  </ul>
                </SubsectionContent>
              </div>

              <div className="subsection">
                <SubsectionHeader subsectionKey="tips-6.3" title="6.3 Planning Trips with Pack-Its" />
                <SubsectionContent subsectionKey="tips-6.3">
                  <ul>
                    <li><strong>Start with Notes:</strong> Capture ideas as notes first, then convert to Pack-Its</li>
                    <li><strong>Use Trip Mood:</strong> When unsure where to go, let Trip Mood suggest destinations</li>
                    <li><strong>Combine Journeys:</strong> Add relevant journeys to your Explorer Board before creating Pack-Its</li>
                    <li><strong>Chat Assistance:</strong> Use the chat widget for planning help and recommendations</li>
                  </ul>
                </SubsectionContent>
              </div>

              <div className="subsection">
                <SubsectionHeader subsectionKey="tips-6.4" title="6.4 Maximizing Explorer Board Usage" />
                <SubsectionContent subsectionKey="tips-6.4">
                  <ul>
                    <li><strong>Browse Regularly:</strong> Check the Explorer Board for new journey suggestions</li>
                    <li><strong>Track Progress:</strong> Mark locations as completed to track your journey progress</li>
                    <li><strong>Multiple Journeys:</strong> Keep multiple journeys active to see how they overlap</li>
                    <li><strong>Use Filters:</strong> Filter journeys by your interests to find relevant ones quickly</li>
                  </ul>
                </SubsectionContent>
              </div>

              <div className="subsection">
                <SubsectionHeader subsectionKey="tips-6.5" title="6.5 Mobile Usage Tips" />
                <SubsectionContent subsectionKey="tips-6.5">
                  <ul>
                    <li><strong>Email Notes:</strong> Use email to capture notes on mobile devices</li>
                    <li><strong>Responsive Design:</strong> Ideal Scribe works on mobile browsers</li>
                    <li><strong>Quick Access:</strong> Bookmark the site for quick access</li>
                  </ul>
                </SubsectionContent>
              </div>
              </div>
            </div>
          )}
        </section>

        {/* Troubleshooting & FAQ Section */}
        <section className={`section ${expandedSections.troubleshooting ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="troubleshooting" title="7. Troubleshooting & FAQ" />
          {expandedSections.troubleshooting && (
            <div className="section-content-wrapper">
              <div className="section-text">
              <div className="subsection">
                <SubsectionHeader subsectionKey="troubleshooting-7.1" title="7.1 Common Questions" />
                <SubsectionContent subsectionKey="troubleshooting-7.1">
                  <div className="subsection">
                    <SubsectionHeader subsectionKey="troubleshooting-password" title="How do I reset my password?" level={4} />
                    <SubsectionContent subsectionKey="troubleshooting-password">
                      <p>Use the "Forgot Password" link on the sign-in page to reset your password via email.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="troubleshooting-map-notes" title="Why aren't my notes showing on the map?" level={4} />
                    <SubsectionContent subsectionKey="troubleshooting-map-notes">
                      <p>Notes need location information to appear on the map. Make sure your notes include an address or location tag.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="troubleshooting-journeys-active" title="How many journeys can I have active at once?" level={4} />
                    <SubsectionContent subsectionKey="troubleshooting-journeys-active">
                      <p>You can have up to 3 journeys active simultaneously. To activate a different journey, first deactivate one of your current active journeys.</p>
                      <p><strong>Important:</strong> Only active journeys automatically calculate and update their progress percentage. If you want a journey to track your completion automatically, make sure it's set to active. Inactive journeys won't update their progress even when you add new notes with location data.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="troubleshooting-journey-progress" title="Why isn't my journey progress updating?" level={4} />
                    <SubsectionContent subsectionKey="troubleshooting-journey-progress">
                      <p>If your journey progress isn't updating automatically, check the following:</p>
                      <ol>
                        <li><strong>Is the journey set to active?</strong> Only active journeys automatically calculate progress. Click the play circle icon to activate the journey (you can have up to 3 active at once).</li>
                        <li><strong>Do your notes have location information?</strong> Progress is calculated by comparing your notes' locations with the journey's locations. Make sure your notes include addresses or location tags.</li>
                        <li><strong>Try the Recalculate button:</strong> Click the sync icon (↻) next to the journey to manually refresh the progress calculation. This can help if progress seems stuck or incorrect.</li>
                        <li><strong>Check journey settings:</strong> If you've manually set a completion percentage in the journey settings, it may override automatic calculation. Clear the manual percentage to allow automatic updates.</li>
                      </ol>
                      <p>For more information about journey buttons, see section 4.7 "Understanding Journey Buttons" in this guide.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="troubleshooting-edit-notes" title="Can I edit notes after creating them?" level={4} />
                    <SubsectionContent subsectionKey="troubleshooting-edit-notes">
                      <p>Yes! Click on any note to view its details, then click "Edit" to modify the note.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="troubleshooting-delete-packit" title="How do I delete a Pack-It?" level={4} />
                    <SubsectionContent subsectionKey="troubleshooting-delete-packit">
                      <p>Open the Pack-It details page and look for the delete option in the settings menu.</p>
                    </SubsectionContent>
                  </div>

                  <div className="subsection">
                    <SubsectionHeader subsectionKey="troubleshooting-journey-diff" title="What's the difference between &quot;Add Journey&quot; and &quot;Create Journey&quot;?" level={4} />
                    <SubsectionContent subsectionKey="troubleshooting-journey-diff">
                      <p>"Add Journey" adds an existing journey from the system to your Explorer Board. "Create Journey" lets you create a brand new journey based on your preferences.</p>
                    </SubsectionContent>
                  </div>
                </SubsectionContent>
              </div>

              <div className="subsection">
                <SubsectionHeader subsectionKey="troubleshooting-7.2" title="7.2 Getting Help" />
                <SubsectionContent subsectionKey="troubleshooting-7.2">
                  <p>If you need additional assistance:</p>
                  <ul>
                    <li>Visit the <Link to="/support">Support</Link> page</li>
                    <li>Check the tutorial modal (available when you first log in)</li>
                    <li>Review this Startup Guide for detailed instructions</li>
              </ul>
                </SubsectionContent>
              </div>

              <div className="subsection">
                <SubsectionHeader subsectionKey="troubleshooting-7.3" title="7.3 Support Resources" />
                <SubsectionContent subsectionKey="troubleshooting-7.3">
                  <ul>
                    <li><strong>Support Page:</strong> <Link to="/support">/support</Link></li>
                    <li><strong>Terms of Service:</strong> <Link to="/terms-of-service">/terms-of-service</Link></li>
                    <li><strong>Privacy Policy:</strong> <Link to="/privacy-policy">/privacy-policy</Link></li>
                  </ul>
                </SubsectionContent>
              </div>
              </div>
            </div>
          )}
        </section>

        {/* Conclusion */}
        <section className={`section ${expandedSections.conclusion ? 'expanded' : 'collapsed'}`}>
          <SectionHeader sectionKey="conclusion" title="8. You're Ready to Go!" />
          {expandedSections.conclusion && (
            <div className="section-content-wrapper">
              <div className="section-text">
              <p>Congratulations! You now have a comprehensive understanding of Ideal Scribe. Here's your action plan to get started:</p>
              <ol>
                <li>Complete your Account Settings (set home location and customize colors)</li>
                <li>Create your first note (try both the web interface and email method)</li>
                <li>Explore the Explorer Board and add a journey that interests you</li>
                <li>Create your first Pack-It (start with Quick Trip for simplicity)</li>
                <li>Experiment with filters, tags, and map features</li>
              </ol>
              <p>Remember: Ideal Scribe is designed to grow with you. Start simple, and as you become more comfortable, explore the advanced features. Happy planning!</p>
            </div>
          </div>
          )}
        </section>
      </div>

      <footer className="footer">
        <div className="footer-left">
          <span>© {new Date().getFullYear()} Ideal Scribe. All rights reserved.</span>
        </div>
        <nav className="footer-links">
          <Link to="/terms-of-service">Terms of Service</Link>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/support">Support</Link>
          <div className="social-links">
            <a href="https://medium.com" target="_blank" rel="noopener noreferrer"><FaMedium /></a>
            <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer"><FaFacebook /></a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"><FaTwitter /></a>
          </div>
        </nav>
      </footer> 
    </div>
  );
}

export default StartupGuide;
